import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import CloseIcon from "../../assets/icons/CloseIcon";
import { Buffer } from "buffer";
import ZoomOut from "../../assets/icons/ZoomOut";
import ZoomIn from "../../assets/icons/ZoomIn";
import PrimaryButton from "../buttons/PrimaryButton";

export type ImageSize = {
  width: number;
  height: number;
};

const ImageCrop = ({
  title = "Crop Image",
  sourceImage,
  size: {
    crop = {
      width: 100,
      height: 100
    },
    preview = {
      width: 120,
      height: 150
    }
  },
  minZoom = 50,
  maxZoom = 150,
  onSubmit,
  onCancel
}: {
  title?: string;
  sourceImage: File;
  size: {
    crop?: ImageSize;
    preview?: ImageSize;
  };
  minZoom?: number;
  maxZoom?: number;
  onSubmit: (value: Blob) => void;
  onCancel: () => void;
}) => {
  const [isDrag, setIsDrag] = useState(false);
  const [image, setImage] = useState<string | undefined>();
  const reader = useMemo(() => new FileReader(), []);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [zoom, setZoomState] = useState(100);
  const [offset, setOffset] = useState({
    x: 0,
    y: 0
  });

  // Capture the current image position and return the data blob
  const captureImage = () => {
    const preview = previewRef.current;

    // Return undefined if the preview canvas is unavailable
    if (preview === null) {
      return;
    }

    drawImageOnTo(previewRef, canvasRef);

    // Split the dataURL of the canvas into its two main components
    const url = preview.toDataURL();
    const splitPos = url.indexOf(",");
    const data = [url.slice(0, splitPos), url.slice(splitPos + 1)];

    // Convert the clean base64 string into bytes
    const buffer = Buffer.from(data[1], "base64");

    // Tidy up the canvas
    preview.getContext("2d")?.clearRect(0, 0, preview.width, preview.height);

    return new Blob([buffer], {
      type: "image/png"
    });
  };

  const normalise = (
    source: {
      width: number;
      height: number;
    },
    target: {
      width: number;
      height: number;
    },
    zoom: number
  ) => {
    const result = {
      width: 0,
      height: 0
    };

    if (source.width < source.height) {
      result.width = target.width * zoom;
      result.height = source.height * (result.width / source.width);
    } else {
      result.height = target.height * zoom;
      result.width = source.width * (result.height / source.height);
    }

    return result;
  };

  /**
   * Moves the image by the given offset
   */
  const moveImage = useCallback((offset: { x: number; y: number }) => {
    setOffset(position => ({
      x: position.x + offset.x,
      y: position.y + offset.y
    }));
  }, []);

  // Draw the image at the correct position on the given canvas
  const drawImageOnTo = useCallback(
    (
      canvasRef: React.RefObject<HTMLCanvasElement>,
      canvasSize: React.RefObject<HTMLCanvasElement>
    ) => {
      const size = canvasSize.current;
      const canvas = canvasRef.current;
      const image = imageRef.current;

      if (canvas !== null && image !== null && size !== null) {
        const context = canvas.getContext("2d");
        const { width: imageWidth, height: imageHeight } = normalise(
          image,
          size,
          zoom / 100
        );

        context?.clearRect(0, 0, canvas.width, canvas.height);
        context?.drawImage(
          image,
          canvas.width / 2 - imageWidth / 2 + offset.x,
          canvas.height / 2 - imageHeight / 2 + offset.y || 0,
          imageWidth,
          imageHeight
        );
      }
    },
    [offset, zoom]
  );

  // Read in the contents of the image file so we can view the image
  useEffect(() => {
    try {
      reader.onload = e => {
        setImage(e.target?.result as string | undefined);
      };
      reader.readAsDataURL(sourceImage);
    } catch (err) {}
  }, [reader, sourceImage, drawImageOnTo]);

  // Update the image on the canvas when the position changes
  useEffect(() => {
    drawImageOnTo(canvasRef, canvasRef);
  }, [image, offset, drawImageOnTo]);

  return (
    <div className="absolute left-0 top-0 z-30 flex h-full w-full select-none items-center justify-center">
      <section className="w-fit rounded-[10px] bg-white px-4 pb-3 pt-4 shadow-ctb-modal-2">
        <div className="flex items-center justify-between">
          <p className="text-xl">{title}</p>
          <div onClick={onCancel}>
            <CloseIcon className="stroke-ctb-grey-300" />
          </div>
        </div>

        <p className="font-sm mb-1 mt-3 text-center text-[#DC2626]">
          Drag to crop & reposition
        </p>

        <div
          className="relative mx-4 rounded-[10px] bg-slate-50 stroke-ctb-blue-400 p-4"
          onMouseLeave={() => setIsDrag(false)}
          onMouseDown={() => setIsDrag(true)}
          onMouseUp={() => setIsDrag(false)}
          onMouseMove={e => {
            if (isDrag) {
              moveImage({
                x: e.movementX,
                y: e.movementY
              });
            }
          }}
          onWheel={e => {
            setZoomState(zoom => {
              const newZoom = zoom + e.deltaY;

              if (newZoom < minZoom) {
                return minZoom;
              }

              if (newZoom > maxZoom) {
                return maxZoom;
              }

              return newZoom;
            });
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <canvas
            ref={canvasRef}
            width={preview.width}
            height={preview.height}
            className="rounded-[10px] bg-ctb-blue-100"
          />
          <img
            ref={imageRef}
            src={image}
            alt="preview"
            onLoad={() => drawImageOnTo(canvasRef, canvasRef)}
            hidden
          />

          <div className="absolute left-[2%] top-[2%] flex h-[96%] w-[96%] items-center justify-center overflow-hidden">
            <canvas
              ref={previewRef}
              width={crop.width}
              height={crop.height}
              className={`rounded-full shadow-[0px_0px_0px_10000px_rgba(255,255,255,0.72)]`}
            />
          </div>
        </div>

        <div className="mx-5 mt-3 flex items-center justify-evenly gap-2">
          <ZoomOut className="h-4 stroke-ctb-blue-400" />
          <input
            type="range"
            min={minZoom}
            max={maxZoom}
            value={zoom}
            onChange={e => setZoomState(parseInt(e.target.value))}
            className="flex-grow"
          />
          <ZoomIn className="h-4 stroke-ctb-blue-400" />
        </div>

        <div className="mt-10 flex w-full items-center justify-end gap-1">
          <PrimaryButton
            className="border-[2px] border-ctb-grey-100 bg-white !text-black transition hover:!bg-ctb-grey-50"
            onClick={onCancel}
          >
            Cancel
          </PrimaryButton>

          <PrimaryButton
            className="border-[2px] border-ctb-blue-200 transition hover:opacity-90"
            onClick={() => {
              const image = captureImage();

              if (image !== undefined) {
                // const url = URL.createObjectURL(image);
                // window.open(url);
                onSubmit(image);
              }
            }}
          >
            Done
          </PrimaryButton>
        </div>
      </section>
    </div>
  );
};

export default ImageCrop;
